import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Button, Layout } from 'antd';
import { toast } from 'react-toastify';
import {
  AiOutlineMenu,
  AiOutlineDashboard,
  AiOutlineShoppingCart,
  AiOutlineUser,
  AiOutlineBoxPlot,
  AiOutlineTag,
  AiOutlineSetting,
  AiOutlineLogout,
  AiOutlineClose,
  AiOutlineMore
} from "react-icons/ai";
import { FaRegCircleUser } from "react-icons/fa6";

const { Header, Content } = Layout;

const AdminPanel = () => {
  const [isSidebarVisible, setIsSidebarVisible] = useState(false); // Only for desktop collapse or mobile slide (now limited)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false); // Desktop collapse only
  const [isMoreOpen, setIsMoreOpen] = useState(false); // For "More" dropdown in bottom nav
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Session check
  useEffect(() => {
    const sessionItem = sessionStorage.getItem('H7Z4L9VD2XK5JW8QRYT6');
    if (!sessionItem) {
      setUser(null);
      return;
    }

    try {
      const decodedItem = atob(sessionItem);
      const parsedUser = JSON.parse(decodedItem);
      setUser(parsedUser);
    } catch (error) {
      console.error('Error decoding session:', error);
      sessionStorage.removeItem('H7Z4L9VD2XK5JW8QRYT6');
      toast.error("Invalid session.");
      setUser(null);
    }
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem('H7Z4L9VD2XK5JW8QRYT6');
    toast.success('Logged out successfully');
    navigate('/admin-login');
  };

  const toggleSidebar = () => {
    if (window.innerWidth >= 1024) {
      setIsSidebarCollapsed(!isSidebarCollapsed);
    }
    // No sidebar slide on mobile/tablet anymore
  };

  // Prevent scroll only if desktop sidebar is involved (not needed now for mobile)
  useEffect(() => {
    if (isSidebarVisible && window.innerWidth < 1024) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isSidebarVisible]);

  const mainMenuItems = [
    { key: 'dashboard', icon: <AiOutlineDashboard />, label: 'Dashboard', path: '/admin-panel/dashboard' },
    { key: 'products', icon: <AiOutlineBoxPlot />, label: 'Products', path: '/admin-panel/products' },
    { key: 'orders', icon: <AiOutlineShoppingCart />, label: 'Orders', path: '/admin-panel/orders' },
    { key: 'customers', icon: <AiOutlineUser />, label: 'Customers', path: '/admin-panel/customers' },
  ];

  const moreMenuItems = [
    { key: 'categories', icon: <AiOutlineTag />, label: 'Packages', path: '/admin-panel/packages' },
    { key: 'settings', icon: <AiOutlineSetting />, label: 'Settings', path: '/admin-panel/settings' },
  ];

  const handleMenuClick = (path) => {
    navigate(path);
    setIsMoreOpen(false); // Close more dropdown if open
    // No sidebar close needed since it's not shown on mobile
  };

  const isActive = (path) => location.pathname.startsWith(path);

  return (
    <Layout className="min-h-screen bg-gray-50">
      {/* Light Pink Header */}
      <Header className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 h-16 bg-pink-100 shadow-md border-b border-pink-200">
        <div className="flex items-center">
          {/* Hamburger only for desktop collapse */}
          <Button
            type="text"
            icon={<AiOutlineMenu className="text-pink-700 text-2xl" />}
            onClick={toggleSidebar}
            className="lg:block hidden"
          />
        </div>
        <Button
          icon={<AiOutlineLogout className="text-pink-700 mr-2" />}
          onClick={handleLogout}
          className="bg-red-500 text-white hover:bg-red-700 text-pink-800 border-none font-semibold"
        >
          Logout
        </Button>
      </Header>

      {/* Desktop Sidebar Only (Collapsible) */}
      <div
        className={`hidden lg:block fixed left-0 top-16 bottom-0 bg-pink-100 shadow-2xl overflow-y-auto transition-all duration-300 border-r border-pink-200 ${
          isSidebarCollapsed ? 'w-20' : 'w-64'
        }`}
      >
        <SidebarContent 
          user={user} 
          menuItems={[...mainMenuItems, ...moreMenuItems]} 
          isActive={isActive} 
          handleMenuClick={handleMenuClick} 
          collapsed={isSidebarCollapsed} 
        />
      </div>

      {/* Main Content Area */}
      <Layout className={`transition-all duration-300 pt-16 ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <Content className="min-h-screen bg-gray-50 pb-20 lg:pb-0">
          <div className="bg-white shadow-lg border border-gray-100 min-h-full">
            <Outlet />
          </div>
        </Content>
      </Layout>

      {/* Bottom Navigation for Mobile & Tablet (<1024px) */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-pink-200 shadow-2xl lg:hidden z-40">
        <nav className="grid grid-cols-5 gap-1 py-2 relative">
          {/* Main 4 Items */}
          {mainMenuItems.map((item) => (
            <button
              key={item.key}
              onClick={() => handleMenuClick(item.path)}
              className={`flex flex-col items-center justify-center py-3 rounded-lg transition-all ${
                isActive(item.path)
                  ? 'text-pink-600 bg-pink-50'
                  : 'text-gray-600 hover:text-pink-500'
              }`}
            >
              <span className="text-2xl">{item.icon}</span>
              <span className="text-xs mt-1 font-medium">{item.label}</span>
            </button>
          ))}

          {/* More Button with Dropdown */}
          <div className="relative">
            <button
              onClick={() => setIsMoreOpen(!isMoreOpen)}
              className={`w-full flex flex-col items-center justify-center py-3 rounded-lg transition-all ${
                isMoreOpen ? 'text-pink-600 bg-pink-50' : 'text-gray-600 hover:text-pink-500'
              }`}
            >
              <span className="text-2xl"><AiOutlineMore /></span>
              <span className="text-xs mt-1 font-medium">More</span>
            </button>

            {/* Dropdown List */}
            {isMoreOpen && (
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 bg-white rounded-xl shadow-2xl border border-pink-200 overflow-hidden">
                <div className="py-2">
                  {moreMenuItems.map((item) => (
                    <button
                      key={item.key}
                      onClick={() => handleMenuClick(item.path)}
                      className={`w-full flex items-center gap-4 px-6 py-4 transition-all ${
                        isActive(item.path)
                          ? 'bg-pink-500 text-white font-semibold'
                          : 'text-gray-800 hover:bg-pink-50'
                      }`}
                    >
                      <span className="text-xl">{item.icon}</span>
                      <span className="font-medium">{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </nav>
      </div>

      {/* Overlay when More is open (to close on outside tap) */}
      {isMoreOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-30 lg:hidden"
          onClick={() => setIsMoreOpen(false)}
        />
      )}
    </Layout>
  );
};

// Sidebar Content - Only used for Desktop
const SidebarContent = ({ user, menuItems, isActive, handleMenuClick, collapsed }) => (
  <div className={`p-4 ${collapsed ? 'pt-8' : 'p-6'}`}>
    {/* Profile */}
    <div className={`flex flex-col items-center mb-10 pb-8 border-b ${collapsed ? 'border-pink-200 pb-6' : 'border-pink-200'}`}>
      {user?.profilePic ? (
        <img 
          src={user.profilePic} 
          alt="Admin" 
          className={`rounded-full ${collapsed ? 'w-12 h-12 border-2 border-pink-300' : 'w-20 h-20 border-4 border-pink-300'}`} 
        />
      ) : (
        <FaRegCircleUser className={` ${collapsed ? 'text-4xl text-pink-600' : 'text-6xl text-pink-700'}`} />
      )}
      {!collapsed && (
        <>
          <p className="text-pink-800 text-xl font-bold mt-4">{user?.name || 'ADMIN'}</p>
          <p className="text-pink-600 text-sm">admin@eveya.com</p>
        </>
      )}
    </div>

    {/* Menu */}
    <nav className="space-y-2">
      {menuItems.map((item) => (
        <button
          key={item.key}
          onClick={() => handleMenuClick(item.path)}
          className={`w-full flex items-center ${collapsed ? 'justify-center py-4' : 'gap-4 px-6 py-4'} rounded-xl transition-all ${
            isActive(item.path)
              ? 'bg-pink-500 text-white shadow-md font-semibold'
              : 'text-gray-800 hover:bg-pink-50'
          }`}
        >
          <span className={`text-2xl ${isActive(item.path) ? 'text-white' : 'text-pink-700'}`}>{item.icon}</span>
          {!collapsed && <span className="text-base font-medium">{item.label}</span>}
        </button>
      ))}
    </nav>
  </div>
);

export default AdminPanel;